import {
  DESKTOP_MIN_WIDTH,
  MOBILE_MIN_WIDTH,
  TABLET_MAX_WIDTH,
} from 'constants';
import useCommonStore from 'hooks/useCommonStore';
import useCountdownToAction from 'hooks/useCountdownToAction';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import useScrollToTop from 'hooks/useScrollToTop';
import styled from 'styled-components';
import { paragraphMixin } from 'styles';

const LoadingScreen = ({ pageYOffset, loadingStyles = {} }) => {
  const { t } = useLang();
  const { loadingMessage, isLoading, setLoading, setLoadingMessage } =
    useCommonStore();
  const { isDesktop } = useDetectDevice();

  useScrollToTop();

  useCountdownToAction(
    0,
    isLoading,
    () => {
      setLoading(false);
      setLoadingMessage();
    },
    [isLoading],
    10,
    true
  );

  const renderBackdrop = () => {
    if (!isDesktop && loadingMessage) return null;

    return (
      <LoadingStyled
        $isTransparent={!loadingMessage}
        $pageYOffset={pageYOffset}
        style={{ ...loadingStyles }}
      />
    );
  };

  const renderSpinner = () => {
    return (
      <SpinnerWrap $isTransparent={!loadingMessage} $pageYOffset={pageYOffset}>
        <SpinnerStyled></SpinnerStyled>
        {loadingMessage && <Message>{t(loadingMessage)}</Message>}
      </SpinnerWrap>
    );
  };

  return (
    <>
      {renderBackdrop()}
      {renderSpinner()}
    </>
  );
};

const LoadingStyled = styled.div`
  position: absolute;
  width: 100%;
  height: calc(100% + ${(props) => props.$pageYOffset || 0}px);
  background: #212121;
  opacity: 0.25;
  z-index: 10000;
  top: 0;
`;

const SpinnerWrap = styled.div`
  position: absolute;
  z-index: 10001;
  padding: 16px;
  display: grid;
  justify-items: center;
  background: #fff;
  border-radius: 8px;
  top: calc(50% + ${(props) => props.$pageYOffset || 0}px - 73px);
  left: 16px;
  width: calc(100% - 32px);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    top: calc(50% + ${(props) => props.$pageYOffset || 0}px - 73px);
    left: calc(50% - 218px);
    width: 436px;
  }

  ${(props) =>
    props.$isTransparent
      ? `
      top: calc(50% + ${props.$pageYOffset || 0}px - 25px)!important;
      background: transparent;
      padding: 0px;
    `
      : `
      @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${
          TABLET_MAX_WIDTH - 1
        }px) {
          top: 70px;
          width: 100%;
          left: 0px;
          height: calc(100dvh - 70px);
          border-radius: 0px;
          padding-block: 66%;
        }
      `};
`;
const SpinnerStyled = styled.div`
  width: 50px;
  --b: 7px;
  aspect-ratio: 1;
  border-radius: 50%;
  padding: 2px;
  background: conic-gradient(#0000 0%, var(--bg-primary)) content-box;
  -webkit-mask: repeating-conic-gradient(
      #000 1deg 45deg,
      #0000 45deg 50deg,
      #000 46deg 90deg,
      #0000 90deg 95deg,
      #000 91deg 135deg,
      #0000 135deg 140deg,
      #000 136deg 180deg,
      #0000 180deg 185deg,
      #000 181deg 225deg,
      #0000 225deg 230deg,
      #000 226deg 270deg,
      #0000 270deg 275deg,
      #000 271deg 315deg,
      #0000 315deg 320deg,
      #000 316deg 360deg
    ),
    radial-gradient(
      farthest-side,
      #0000 calc(100% - var(--b) - 4px),
      #000 calc(100% - var(--b))
    );
  -webkit-mask-composite: destination-in;
  mask-composite: intersect;

  animation: s4 1s infinite steps(8);
  @keyframes s4 {
    to {
      transform: rotate(1turn);
    }
  }
`;
const Message = styled.p`
  ${paragraphMixin};
  font-weight: 500;
  margin-top: 24px;
  text-align: center;
`;

export default LoadingScreen;
